import { useCallback } from 'react';
import { useAttachmentsUploadStore } from '../attachments-upload-store-provider';
import { AttachmentsPanelType } from '../types';
import { AttachmentUpload } from '../attachments-upload-store';
import { MAX_NUM_OF_ATTACHMENTS_PER_EVENT, MAX_NUM_OF_ATTACHMENTS_PER_BOLO } from '../constants';

interface UseUploadValidationParams {
    entityType: AttachmentsPanelType;
    attachments: unknown[] | undefined;
    uploads: AttachmentUpload[];
    maxConcurrentUploads: number;
}

interface ValidationResult {
    isValid: boolean;
    error?: string;
}

export function useUploadValidation({
    entityType,
    attachments,
    uploads,
    maxConcurrentUploads,
}: UseUploadValidationParams) {
    const uploadStore = useAttachmentsUploadStore();

    const maxAttachments =
        entityType === 'CAD_AGENCY_EVENT'
            ? MAX_NUM_OF_ATTACHMENTS_PER_EVENT
            : MAX_NUM_OF_ATTACHMENTS_PER_BOLO;

    const validateConcurrentUploads = useCallback(
        function checkConcurrentUploadLimit(): ValidationResult {
            const activeCount = uploadStore.getActiveUploadCount();
            if (activeCount >= maxConcurrentUploads) {
                return {
                    isValid: false,
                    error: `Upload limit reached. You can have a maximum of ${maxConcurrentUploads} uploads in progress.`,
                };
            }
            return { isValid: true };
        },
        [uploadStore, maxConcurrentUploads]
    );

    const validateMaxAttachments = useCallback(
        function checkMaxAttachmentsLimit(): ValidationResult {
            const attachmentsCount = attachments?.length || 0;
            const activeUploadCount = uploads.filter(
                (upload) => upload.status === 'uploading' || upload.status === 'processing'
            ).length;
            const totalCount = attachmentsCount + activeUploadCount + 1;

            if (totalCount > maxAttachments) {
                return {
                    isValid: false,
                    error: `Maximum number of files (${maxAttachments}) has been uploaded.`,
                };
            }
            return { isValid: true };
        },
        [attachments, uploads, maxAttachments]
    );

    return {
        validateConcurrentUploads,
        validateMaxAttachments,
        maxAttachments,
    };
}
